<template>
  <Form
    ref="observer"
    v-slot="{ handleSubmit }"
  >
    <section>
      <ShowErrors
        :errors-messages="errorsMessages"
        :errors-messages-length="errorsMessages.length"
      />
      <TextField
        v-model:value="login"
        :mandatory="true"
        label="login.login"
        name="login.login"
        :testid="'signin-login-input'"
        place-holder="login.login-placeholder"
        rules="min:4"
        @update:value="login = $event"
      />
      <TextField
        v-model:value="password"
        :mandatory="true"
        label="login.pwd"
        name="password"
        :testid="'signin-password-input'"
        place-holder="login.pwd-placeholder"
        type="password"
        @update:value="password = $event"
      />
      <a
        :aria-label="$t('login.pwd-forgotten')"
        @click="isComponentModalActive = true"
      >
        {{ $t('login.pwd-forgotten') }}
      </a>
      <ShowErrors
        :password="password"
        :warning-messages="warningMessages"
        :warning-messages-length="warningMessages.length"
        :danger-messages="dangerMessages"
        :danger-messages-length="dangerMessages.length"
      />
      <TextField
        v-if="verificationKeyVisible"
        v-model:value="verificationKey"
        :mandatory="true"
        label="login.verificationKey"
        name="verificationKey"
        place-holder="login.verificationKey-placeholder"
        rules="length:12"
        @update:value="verificationKey = $event"
      >
        <template #right>
          <a
            class="resend-key-link"
            :class="{ 'is-disabled': resendCooldown > 0 || isSendingKey }"
            :aria-disabled="resendCooldown > 0 || isSendingKey"
            @click.prevent="resendValidationKey()"
          >
            {{ resendCooldown > 0
              ? $t('login.lost-pwd.resend-cooldown', { sec: resendCooldown })
              : $t('login.resend-validation-key') }}
          </a>
        </template>
      </TextField>
    </section>

    <div class="buttons">
      <b-button
        :aria-label="$t('login.aria-btn-login')"
        aria-description="login.signIn"
        data-testid="signin-submit-button"
        icon-left="sign-in-alt"
        type="is-primary"
        @click="handleSubmit(submit)"
      >
        {{ $t("login.signin") }}
      </b-button>
    </div>

    <b-modal
      v-model="isComponentModalActive"
      class="modal-step-forgotten-password"
      :destroy-on-hide="false"
      aria-label="Modal forgotten password"
      aria-role="dialog"
      close-button-aria-label="Close"
      has-modal-card
      trap-focus
      append-to-body
      full-screen
    >
      <div class="card">
        <div class="card-content">
          <PasswordLostModal :sign-in="signIn" />
        </div>
      </div>
    </b-modal>
  </Form>
</template>

<script lang="ts">
import services from "@/composable/services";
import { HttpStatusCodes } from "@/utils/HttpUtils";
import { inject, provide } from "vue";
import { useRoute } from "vue-router";
import { sanitizeReturnUrl } from "@/router";
import useBoolean from "@/composable/components/boolean";
import useText from "@/composable/components/text";
import useArray from "@/composable/components/array";
import { Form } from "vee-validate";
import { onBeforeUnmount, ref } from "vue";
import TextField from "@/components/forms/TextField.vue";
import PasswordLostModal from "@/components/login/PasswordLostModal.vue";
import { useI18n } from "vue-i18n";

import ShowErrors from "@/components/application/ShowErrors.vue";
import { BModal } from "buefy";
import { ErrorType } from "@/composable/application/errors";

export default {
  name: "SigIn",
  components: { BModal, ShowErrors, PasswordLostModal, TextField, Form },
  setup() {
    const { refBoolean: verificationKeyVisible } = useBoolean();
    const { refBoolean: isComponentModalActive } = useBoolean();
    const applicationName
      = window.location.href.split("/")[window.location.href.split("/").length - 1];

    const login = inject("login");
    const verificationKey = useText().refText;
    const email = inject("email");
    const password = useText().refText;
    const errorsMessages = useArray().shallowRefArray;
    const warningMessages = useArray().shallowRefArray;
    // Bannieres danger ( rouge ) : compte ferme CLOSED_ACCOUNT - distinct
    // de warningMessages ( orange , reactivable ) car la cloture est
    // definitive ( pas de cle d'activation possible , contact admin requis ) .
    const dangerMessages = useArray().shallowRefArray;
    const { t } = useI18n();
    provide("password", password);
    provide("verificationKey", verificationKey);
    // Le backend repond 402 PAYMENT_REQUIRED pour les comptes en etat
    // INACTIVE_ACCOUNT ( cf OreExceptionHandler.handle(AuthenticationFailure) ) .
    // Body = plain string "INACTIVE_ACCOUNT" ( Spring serialise un String sans
    // wrapping ) , et les params { login , email } sont dans les headers
    // Login / Email . On gere les deux formes de body ( string OU objet )
    // pour rester robuste si un autre endpoint envoie un format object .
    const handleInactiveAccountResponse = async function (errors) {
      const body = await errors.content;
      const code = typeof body === "string"
        ? body
        : (body?.message ?? "INACTIVE_ACCOUNT");
      const headers = errors.httpHeaders;
      const params = {
        login: headers?.get?.("Login") ?? login.value ?? "",
        email: headers?.get?.("Email") ?? email.value ?? "",
      };
      // Assignation ( pas push ) : garantit qu'une seule banniere est
      // affichee a tout instant , meme si 2 reponses 402 arrivent en
      // parallele ( double-clic , resend pendant qu'un signIn precedent
      // n'a pas encore termine son await content ) . Le reset au debut
      // de signIn ne couvre pas les concurrents qui peuvent push apres
      // ce reset .
      warningMessages.value = [
        services.errorsService.getErrorsLoginMessages(code, params),
      ];
    };

    /**
     * Compte ferme ( CLOSED_ACCOUNT 410 ) . Symetrique a
     * {@link #handleInactiveAccountResponse} mais peuple dangerMessages
     * ( rouge ) au lieu de warningMessages ( orange ) , et declenche un
     * toast rouge pour visibilite immediate . Assignation directe ( pas
     * push ) pour garantir une seule banniere meme en cas d'appels
     * concurrents .
     */
    const handleClosedAccountResponse = async function (errors) {
      const body = await errors.content;
      const code = typeof body === "string"
        ? body
        : (body?.message ?? "CLOSED_ACCOUNT");
      const headers = errors.httpHeaders;
      const params = {
        login: headers?.get?.("Login") ?? login.value ?? "",
        email: headers?.get?.("Email") ?? email.value ?? "",
      };
      const msg = services.errorsService.getErrorsLoginMessages(code, params);
      dangerMessages.value = [msg];
      // Toast supprimer le markup HTML eventuel ( <strong>...</strong> ) pour
      // que Buefy ne l'affiche pas en texte brut ; la banniere garde le
      // markup formatte .
      const plain = typeof msg === "string" ? msg.replace(/<[^>]+>/g, "") : "";
      services.alertService.toastErrorMessage(plain);
    };

    const route = useRoute();

    /**
     * Lien "Renvoyer une nouvelle clé par email" affiche a droite du champ
     * cle d'activation . Indispensable pour debloquer un utilisateur dont
     * la cle precedente a expire ou s'est perdue ( pas d'autre moyen de
     * regenerer la cle depuis l'ecran de login ) . Memes semantiques que
     * dans PasswordLostModal : cooldown 10s anti-spam SMTP , libelle
     * disable pendant l'envoi en cours .
     *
     * <p>Implementation : on rappelle {@code signIn} avec les memes
     * identifiants -> le backend ( cf {@code AuthenticationService.login}
     * cas {@code pending} / {@code idle} ) renvoie automatiquement une
     * nouvelle cle a chaque tentative et re-leve 402 PAYMENT_REQUIRED .
     * On capte le 402 comme succes attendu ( la cle a bien ete envoyee )
     * sans declencher la banniere INACTIVE_ACCOUNT redondante .
     */
    const RESEND_COOLDOWN_SECONDS = 10;
    const resendCooldown = ref(0);
    const isSendingKey = ref(false);
    let cooldownInterval: ReturnType<typeof setInterval> | null = null;

    function startCooldown() {
      resendCooldown.value = RESEND_COOLDOWN_SECONDS;
      if (cooldownInterval) clearInterval(cooldownInterval);
      cooldownInterval = setInterval(() => {
        resendCooldown.value -= 1;
        if (resendCooldown.value <= 0) {
          if (cooldownInterval) {
            clearInterval(cooldownInterval);
            cooldownInterval = null;
          }
          resendCooldown.value = 0;
        }
      }, 1000);
    }

    onBeforeUnmount(() => {
      if (cooldownInterval) clearInterval(cooldownInterval);
    });

    async function resendValidationKey(): Promise<void> {
      if (resendCooldown.value > 0 || isSendingKey.value) return;
      isSendingKey.value = true;
      try {
        const target = applicationName !== "login" ? applicationName : undefined;
        await services.loginService.signIn(login.value, password.value, target, undefined);
        // signIn ne devrait jamais reussir ici ( user toujours pending ) ;
        // si jamais ca arrive ( ex etat passe entre-temps a active ) , on
        // n'affiche pas de toast d'erreur , l'utilisateur est dans un etat
        // valide .
      } catch (e) {
        const errors = e as ErrorType;
        if (errors?.httpResponseCode === HttpStatusCodes.PAYMENT_REQUIRED) {
          services.alertService.toastSuccess(t("alert.new-validation-key-sent"));
        } else {
          services.alertService.toastServerError(errors);
        }
      } finally {
        startCooldown();
        isSendingKey.value = false;
      }
    }

    const signIn = async function () {
      // Reset l'etat warning : sans ce clear , chaque clic supplementaire sur
      // "Sign in" empile une nouvelle banniere INACTIVE_ACCOUNT identique a
      // la precedente ( cf bug user 33 - 3 bannieres superposees apres 3
      // tentatives ) . Single source of truth pour l'etat warning du form .
      warningMessages.value = [];
      dangerMessages.value = [];
      try {
        // #470 - returnUrl éventuellement posé par le garde router.beforeEach
        // lors de la redirection vers /login. Filtré via sanitizeReturnUrl
        // pour ne garder que les chemins locaux.
        const returnUrl = sanitizeReturnUrl(route.query.returnUrl);
        if (applicationName !== "login") {
          await services.loginService.signIn(login.value, password.value, applicationName, returnUrl);
        }
        else {
          await services.loginService.signIn(login.value, password.value, undefined, returnUrl);
        }
      }
      catch (e) {
        const errors: ErrorType = e as ErrorType;
        if (errors.httpResponseCode === HttpStatusCodes.PAYMENT_REQUIRED) {
          await handleInactiveAccountResponse(errors);
          verificationKeyVisible.value = true;
        }
        else if (errors.httpResponseCode === HttpStatusCodes.GONE) {
          // CLOSED_ACCOUNT : compte ferme , non reactivable par l'utilisateur .
          // On affiche une banniere danger ( meme zone que warningMessages
          // mais coloration rouge via {@code dangerMessages} ) + un toast
          // rouge pour confirmer l'action au moment du clic .
          await handleClosedAccountResponse(errors);
        }
        else if (errors.httpResponseCode === HttpStatusCodes.UNAUTHORIZED) {
          services.alertService.toastError(t("alert.user-unknown"), errors);
        }
        else if (errors.httpResponseCode === HttpStatusCodes.FORBIDDEN) {
          errors.content.then((m) => {
            services.alertService.toastError(t("alert.user-unknown"), {
              content: new Promise(() => m),
            });
          });
        }
        else {
          services.alertService.toastServerError(errors);
        }
      }
    };

    // Issue mismatch frontend/backend ( 10 vs 12 ) :
    // backend AuthenticationService.generateVerificationKey produit
    // 12 hex chars uppercase ( commit e1d1925 - durcissement crypto ) .
    // Format : ^[A-F0-9]{12}$ - regex tolerant qui valide la longueur ET
    // la forme , afin de ne pas reproduire le bug si le backend evolue
    // ( ex 16 chars ) ou qu'un utilisateur paste avec espaces / dashes .
    const VERIFICATION_KEY_PATTERN = /^[A-F0-9]{12}$/i;

    const activateLogin = async function () {
      if (VERIFICATION_KEY_PATTERN.test(verificationKey.value)) {
        try {
          await services.loginService.modifAcount({
            login: login.value,
            password: password.value,
            verificationKey: verificationKey.value,
          });
          services.alertService.toastSuccess(t("alert.user-updated", email.value));
          await signIn();
        }
        catch (error) {
          // Bug user 33 : toast rouge "vide" quand la cle est invalide .
          // Cause : le backend renvoie body = plain string ( ex
          // "BAD_VALIDATION_KEY" via OreExceptionHandler ) , et l'ancien
          // catch passait `{}` au toast -> aucun texte affiche . On lit le
          // body comme string ou objet , on resoud le code i18n correspondant ,
          // et on fallback sur le message generique "cle invalide" si rien
          // d'exploitable . Le toast est ainsi toujours pedagogique .
          let backendMsg = "";
          try {
            const err = error as ErrorType | undefined;
            const body = await err?.content;
            const code = typeof body === "string" ? body : (body?.message ?? "");
            const params = (typeof body === "object" && body !== null) ? body.params : undefined;
            if (code) {
              backendMsg = services.errorsService.getErrorsLoginMessages(code, params) ?? "";
            }
          }
          catch { /* swallow - fallback message ci-dessous */ }
          services.alertService.toastError(
            backendMsg || t("alert.verification-key-invalid"),
            {},
          );
        }
      } else if (verificationKey.value.length > 0) {
        // Feedback explicite si l'utilisateur saisit une cle invalide
        // ( evite le silent block qui a cause l'incident initial ) .
        services.alertService.toastError(t("alert.verification-key-invalid"), {});
      }
    };

    // Submit dispatcher : un seul bouton "Sign in" pilote 2 flux selon
    // que la verificationKey est visible ou non . Avant ce refacto ,
    // activateLogin() etait appele automatiquement sur chaque @update:value
    // du champ verificationKey - ce qui declenchait la validation de la cle
    // des le paste / dernier char saisi , sans clic explicit sur le bouton .
    // L'utilisateur ne pouvait pas relire / corriger sa cle avant submit .
    // Maintenant : verificationKey saisie passive , et le bouton dispatche
    // vers activateLogin ( si cle visible + non vide ) ou signIn ( login
    // classique ) .
    const submit = async function () {
      if (verificationKeyVisible.value && verificationKey.value.length > 0) {
        await activateLogin();
      }
      else {
        await signIn();
      }
    };

    function updateForm(event) {
      if (event.login) {
        login.value = event.login;
      }
      if (event.email) {
        email.value = event.email;
      }
      if (event.verificationKey) {
        verificationKey.value = event.verificationKey;
      }
      if (event.password) {
        password.value = event.password;
      }
    }

    return {
      // from '@/composition/components/boolean'
      verificationKeyVisible,
      isComponentModalActive,

      login,
      password,
      email,
      verificationKey,

      errorsMessages,
      warningMessages,
      dangerMessages,

      activateLogin,
      signIn,
      submit,
      handleInactiveAccountResponse,
      updateForm,
      applicationName,
      resendValidationKey,
      resendCooldown,
      isSendingKey,
    };
  },
};
</script>
<style lang="scss">
.icon.is-right.has-text-primary.is-clickable {
  color: $primary !important;
  background-color: transparent !important;
}

li {
  list-style-type: none;
}

.step-link {
  background-color: white;
}

/*
 * Lien "Renvoyer une nouvelle clé par email" affiche a droite du champ
 * cle d'activation . Style aligne sur PasswordLostModal pour cohérence
 * visuelle entre les deux flows ( forgot-password vs login pending ) .
 */
.resend-key-link {
  font-size: 0.85rem;
  color: var(--oa-primary, #00ae99);
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  font-weight: 500;
}
.resend-key-link:hover:not(.is-disabled) {
  text-decoration: underline;
}
.resend-key-link.is-disabled {
  color: var(--fg-muted, #999);
  cursor: not-allowed;
  pointer-events: none;
}
</style>
